import { isEqual } from 'lodash'
import { logWarn } from 'lib/logging'
import type { Appearance, BackgroundPreset, FontPreset } from 'app/lib/forms/appearance'
import { BACKGROUND_PRESETS, DEFAULT_APPEARANCE, FONT_PRESETS } from 'app/lib/forms/appearance'
import type { FormCategory, FormQuestion, QuestionKind, QuestionOption, ShowWhen } from 'app/lib/forms/types'
import { NON_ANSWERABLE_KINDS } from 'app/lib/forms/types'

export const QUESTION_KIND_LABELS: Readonly<Record<QuestionKind, string>> = {
  shortText: `Short answer`,
  longText: `Paragraph`,
  date: `Date`,
  phone: `Phone number`,
  email: `Email`,
  address: `Address`,
  singleSelect: `Multiple choice`,
  multiSelect: `Checkboxes`,
  yesNo: `Yes or no`,
  signature: `Signature`,
  initials: `Initials`,
  staticText: `Text block`,
}

export const FORM_CATEGORY_LABELS: Readonly<Record<FormCategory, string>> = {
  registration: `Registration`,
  medicalHistory: `Medical history`,
  consent: `Consent`,
  financialPolicy: `Financial policy`,
  custom: `Other`,
}

/** The contact fields the API's completion step knows how to write. */
/** A yes or no answer that turns the contact's text message consent on or off. */
export const SMS_CONSENT_TARGET = `smsConsent`

export const MAPPING_TARGET_LABELS: Readonly<Record<string, string>> = {
  firstName: `First name`,
  lastName: `Last name`,
  birthDate: `Date of birth`,
  cellPhone: `Cell phone`,
  homePhone: `Home phone`,
  email: `Email`,
  address: `Address`,
  [SMS_CONSENT_TARGET]: `Text message consent`,
}

const TEXT_TARGETS = [`firstName`, `lastName`, `birthDate`, `cellPhone`, `homePhone`, `email`, `address`]
const TEXT_TARGET_KINDS: QuestionKind[] = [`shortText`, `date`, `phone`, `email`, `address`]

/** The contact fields a question of this kind can save to: free text to text fields, yes or no
 *  to consent. Anything else would write an answer the field cannot hold. */
export function mappingTargetsFor(kind: QuestionKind): string[] {
  if (kind === `yesNo`) return [SMS_CONSENT_TARGET]
  return TEXT_TARGET_KINDS.includes(kind) ? TEXT_TARGETS : []
}

export const OPTION_KINDS: readonly QuestionKind[] = [`singleSelect`, `multiSelect`]

/** Kinds whose answer can drive another question's `showWhen`. */
const CONDITION_KINDS: QuestionKind[] = [`yesNo`, `singleSelect`, `multiSelect`]

/** Random rather than derived from the label, so rewording a question never changes its id. */
export function mintId(prefix: `q` | `opt`): string {
  return `${prefix}_${crypto.randomUUID().replace(/-/g, ``).slice(0, 10)}`
}

export function newQuestion(kind: QuestionKind): FormQuestion {
  return {
    id: mintId(`q`),
    kind,
    label: kind === `staticText` ? `` : QUESTION_KIND_LABELS[kind],
    required: !NON_ANSWERABLE_KINDS.includes(kind),
    ...(OPTION_KINDS.includes(kind) ? { options: [{ id: mintId(`opt`), label: `Option 1` }] } : {}),
  }
}

/** A fresh id for the copy and its choices. The mapping stays with the original, so two
 *  questions never write the same contact field. */
export function duplicateQuestion(question: FormQuestion): FormQuestion {
  const { mappingTarget: _mapping, ...rest } = question
  return {
    ...rest,
    id: mintId(`q`),
    ...(question.options ? { options: question.options.map((option) => ({ ...option, id: mintId(`opt`) })) } : {}),
  }
}

/** Keeps the id and everything that still makes sense for the new kind. */
export function changeKind(question: FormQuestion, kind: QuestionKind): FormQuestion {
  const { options, mappingTarget, ...rest } = question
  const nextOptions = OPTION_KINDS.includes(kind) ? (options ?? [{ id: mintId(`opt`), label: `Option 1` }]) : undefined
  const keptTarget = mappingTarget && mappingTargetsFor(kind).includes(mappingTarget) ? mappingTarget : undefined
  return {
    ...rest,
    ...(keptTarget ? { mappingTarget: keptTarget } : {}),
    kind,
    required: NON_ANSWERABLE_KINDS.includes(kind) ? false : rest.required,
    ...(nextOptions ? { options: nextOptions } : {}),
  }
}

/** The values another question can test this one against, in the runtime's stored form. */
export function conditionValues(question: FormQuestion): QuestionOption[] {
  if (question.kind === `yesNo`) {
    return [
      { id: `yes`, label: `Yes` },
      { id: `no`, label: `No` },
    ]
  }
  return question.options ?? []
}

/** Only questions above this one can drive it, which also rules out cycles. */
export function conditionSources(questions: FormQuestion[], index: number): FormQuestion[] {
  return questions.slice(0, index).filter((q) => CONDITION_KINDS.includes(q.kind))
}

/** Questions below the target that would lose their condition if it moved or went away. */
export function dependentsOf(questions: FormQuestion[], questionId: string): FormQuestion[] {
  return questions.filter((q) => q.showWhen?.questionId === questionId)
}

export function moveItem<T>(items: T[], from: number, to: number): T[] {
  if (to < 0 || to >= items.length) return items
  const next = [...items]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

/** Every reason the form cannot be published yet, in question order. Empty means publishable. */
export function publishProblems(name: string, questions: FormQuestion[]): string[] {
  const problems: string[] = []
  if (!name.trim()) problems.push(`Give the form a name.`)
  if (questions.length === 0) problems.push(`Add at least one question.`)
  questions.forEach((question, index) => {
    const position = `Question ${index + 1}`
    if (!question.label.trim()) {
      problems.push(question.kind === `staticText` ? `${position} has no text.` : `${position} has no label.`)
    }
    if (OPTION_KINDS.includes(question.kind)) {
      if (!question.options?.length) problems.push(`${position} needs at least one choice.`)
      else if (question.options.some((o) => !o.label.trim())) problems.push(`${position} has a blank choice.`)
    }
    if (question.showWhen) {
      const source = conditionSources(questions, index).find((q) => q.id === question.showWhen?.questionId)
      if (!source) problems.push(`${position} depends on a question that is no longer above it.`)
      else if (!conditionValues(source).some((v) => v.id === question.showWhen?.equals)) {
        problems.push(`${position} depends on a choice that no longer exists.`)
      }
    }
    const firstWithTarget = questions.findIndex((q) => q.mappingTarget === question.mappingTarget)
    if (question.mappingTarget && firstWithTarget < index) {
      const field = MAPPING_TARGET_LABELS[question.mappingTarget] ?? question.mappingTarget
      problems.push(`Questions ${firstWithTarget + 1} and ${index + 1} both save to ${field}. Keep one.`)
    }
  })
  return problems
}

type Row = Record<string, unknown>

/** The API's JSON scalar arrives as an encoded string, so every reader decodes first. */
export function decodeJson(value: unknown): unknown {
  if (typeof value !== `string`) return value
  try {
    return JSON.parse(value)
  } catch (error) {
    logWarn(`forms builder: unreadable JSON from the API`, error)
    return undefined
  }
}

export const isRow = (value: unknown): value is Row =>
  typeof value === `object` && value !== null && !Array.isArray(value)

const text = (value: unknown): string | undefined => (typeof value === `string` ? value : undefined)

function parseOption(value: unknown): QuestionOption | undefined {
  if (!isRow(value) || !text(value.id)) return undefined
  return { id: value.id as string, label: text(value.label) ?? `` }
}

function parseShowWhen(value: unknown): ShowWhen | undefined {
  if (!isRow(value) || !text(value.questionId) || text(value.equals) === undefined) return undefined
  return { questionId: value.questionId as string, equals: value.equals as string }
}

/** The API hands questions back as untyped JSON with nulls. The runtime wants optional fields. */
export function parseQuestions(value: unknown): FormQuestion[] {
  const json = decodeJson(value)
  if (!Array.isArray(json)) return []
  return json.filter(isRow).flatMap((row) => {
    const id = text(row.id)
    const kind = text(row.kind) as QuestionKind | undefined
    if (!id || !kind || !(kind in QUESTION_KIND_LABELS)) return []
    const options = Array.isArray(row.options)
      ? row.options.map(parseOption).filter((o): o is QuestionOption => o !== undefined)
      : []
    const showWhen = parseShowWhen(row.showWhen)
    const question: FormQuestion = { id, kind, label: text(row.label) ?? ``, required: row.required === true }
    if (text(row.helpText)) question.helpText = row.helpText as string
    if (OPTION_KINDS.includes(kind)) question.options = options
    if (showWhen) question.showWhen = showWhen
    if (text(row.mappingTarget)) question.mappingTarget = row.mappingTarget as string
    return [question]
  })
}

export type PacketFormRef = { formId: string; formVersion: number }
export type PacketBranding = { displayName: string; logoUrl?: string; phone?: string }
export type PacketScreen = { title: string; body: string }

export function parsePacketForms(value: unknown): PacketFormRef[] {
  const json = decodeJson(value)
  if (!Array.isArray(json)) return []
  return json
    .filter(isRow)
    .filter((row) => text(row.formId) && typeof row.formVersion === `number`)
    .map((row) => ({ formId: row.formId as string, formVersion: row.formVersion as number }))
}

export function parseBranding(value: unknown): PacketBranding | undefined {
  const json = decodeJson(value)
  if (!isRow(json) || text(json.displayName) === undefined) return undefined
  return {
    displayName: json.displayName as string,
    ...(text(json.logoUrl) ? { logoUrl: json.logoUrl as string } : {}),
    ...(text(json.phone) ? { phone: json.phone as string } : {}),
  }
}

export function parseAppearance(value: unknown): Appearance {
  const json = decodeJson(value)
  if (!isRow(json)) return DEFAULT_APPEARANCE
  const background = text(json.backgroundPreset)
  const font = text(json.fontPreset)
  return {
    accentColor: text(json.accentColor) ?? DEFAULT_APPEARANCE.accentColor,
    backgroundPreset:
      background && background in BACKGROUND_PRESETS
        ? (background as BackgroundPreset)
        : DEFAULT_APPEARANCE.backgroundPreset,
    fontPreset: font && font in FONT_PRESETS ? (font as FontPreset) : DEFAULT_APPEARANCE.fontPreset,
  }
}

export function parseScreen(value: unknown): PacketScreen | undefined {
  const json = decodeJson(value)
  if (!isRow(json) || text(json.title) === undefined) return undefined
  return { title: json.title as string, body: text(json.body) ?? `` }
}

type VersionRow = { formId: string; formVersion: number; status: string }

export type FormVersions<T extends VersionRow> = {
  formId: string
  /** The newest version, draft or published. What the editor opens. */
  latest: T
  /** The newest published version. What a packet can pin. */
  published?: T
}

/** Collapses one row per version into one entry per form, newest first by version. */
export function groupFormVersions<T extends VersionRow>(rows: T[]): FormVersions<T>[] {
  const byForm = new Map<string, T[]>()
  for (const row of rows) byForm.set(row.formId, [...(byForm.get(row.formId) ?? []), row])
  return [...byForm.entries()].map(([formId, versions]) => {
    const sorted = [...versions].sort((a, b) => b.formVersion - a.formVersion)
    return { formId, latest: sorted[0], published: sorted.find((v) => v.status === `published`) }
  })
}

export const HEX_COLOR = /^#[0-9a-f]{6}$/i

/** Unsaved-change check for editor drafts. Ignores key order, and treats a field set to
 *  undefined the same as one that is absent, which is how the API hands it back. */
export function sameDraft(a: unknown, b: unknown): boolean {
  return isEqual(JSON.parse(JSON.stringify(a ?? null)), JSON.parse(JSON.stringify(b ?? null)))
}
